import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
    Alert,
    Box,
    Button,
    CardContent,
    Grid,
    Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import ModuleEventsBookDialog from "./ModuleEventsBookDialog";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DayJS from "react-dayjs/source";
import { bookEvent, deleteEventBooking } from "../../../../actions/tmsActions";

function isInTheFuture(date) {
    const today = new Date();
    today.setHours(23, 59, 59, 998);

    return date > today;
}

function getAttendanceMessage(status, eventDate) {
    switch (status) {
        case "attended":
            return (
                <Alert
                    severity="success"
                    icon={<CheckCircleOutlineIcon fontSize="medium" />}
                >
                    <strong>Attended</strong> on{" "}
                    <DayJS format="DD MMM YYYY">{eventDate}</DayJS>
                </Alert>
            );
        case "attending":
            return (
                <Alert
                    severity="info"
                    icon={<InfoOutlinedIcon fontSize="medium" />}
                >
                    <strong>Attending</strong> on{" "}
                    <DayJS format="DD MMM YYYY">{eventDate}</DayJS>
                </Alert>
            );
        case "waiting":
            return (
                <Alert
                    severity="info"
                    icon={<InfoOutlinedIcon fontSize="medium" />}
                >
                    <strong>On the waiting list</strong> for event on{" "}
                    <DayJS format="DD MMM YYYY">{eventDate}</DayJS>
                </Alert>
            );
        case "cancelled":
            return (
                <Alert
                    severity="error"
                    icon={<ErrorOutlineIcon fontSize="medium" />}
                >
                    <strong>Booking cancelled</strong> for event on{" "}
                    <DayJS format="DD MMM YYYY">{eventDate}</DayJS>
                </Alert>
            );
        case "did_not_attend":
            return (
                <Alert
                    severity="error"
                    icon={<ErrorOutlineIcon fontSize="medium" />}
                >
                    <strong>Did not attend</strong> on{" "}
                    <DayJS format="DD MMM YYYY">{eventDate}</DayJS>
                </Alert>
            );
        default:
            return (
                <Alert
                    severity="info"
                    icon={<InfoOutlinedIcon fontSize="medium" />}
                >
                    <strong>Attending</strong> on{" "}
                    <DayJS format="DD MMM YYYY">{eventDate}</DayJS>
                </Alert>
            );
    }
}

function ModuleEvents({ moduleId, events, isActive, bookedEvent = {} }) {
    const dispatch = useDispatch();
    const [eventsDialogOpen, setEventsDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    if (!isActive) {
        return null;
    }

    let buttonAction,
        buttonLabel,
        confirmAction,
        confirmTitle,
        confirmMessage,
        visibleEvents;

    const handleConfirm = () => {
        confirmAction();
        setConfirmDialogOpen(false);
    };
    const bookAction = (eventId, eventDate) => {
        dispatch(bookEvent(eventId, moduleId, "C-TMS", eventDate));
    };
    const cancelAction = (attendeeId) => {
        dispatch(deleteEventBooking(attendeeId, moduleId, "C-TMS"));
    };

    let eventMessage = (
        <Alert severity="warning" icon={<WarningAmberIcon fontSize="medium" />}>
            No events available.
        </Alert>
    );

    if (!!events && events.length) {
        if (!bookedEvent) {
            visibleEvents = events.filter(({ dateStart }) =>
                isInTheFuture(new Date(dateStart))
            );
            if (visibleEvents.length > 1) {
                eventMessage = (
                    <Alert
                        severity="info"
                        icon={<InfoOutlinedIcon fontSize="medium" />}
                    >
                        {visibleEvents.length} events available
                    </Alert>
                );
                buttonLabel = "book";
                buttonAction = () => setEventsDialogOpen(true);
            } else if (visibleEvents.length === 1) {
                const { id, dateStart } = visibleEvents.pop();
                eventMessage = (
                    <Alert
                        severity="info"
                        icon={<InfoOutlinedIcon fontSize="medium" />}
                    >
                        Upcoming event on{" "}
                        <DayJS format="DD MMM YYYY">{dateStart}</DayJS>
                    </Alert>
                );
                buttonLabel = "book";
                buttonAction = () => setConfirmDialogOpen(true);
                confirmAction = () => bookAction(id, dateStart);
                confirmTitle = "Book event";
                confirmMessage = "Are you sure you wish to attend this event?";
            }
        } else {
            eventMessage = getAttendanceMessage(
                bookedEvent?.attendance?.status,
                bookedEvent?.dateStart
            );
            if (!!cancelAction && !!bookedEvent?.attendance?.canCancel) {
                buttonLabel = "cancel";
                buttonAction = () => setConfirmDialogOpen(true);
                confirmAction = () => cancelAction(bookedEvent?.attendance?.id);
                confirmTitle = "Cancel booking";
                confirmMessage =
                    "Are you sure you wish to cancel your attendance to this event?";
            }
        }
    }

    return (
        <CardContent>
            <Box className="card-content">
                <Grid container spacing={4}>
                    <Grid item xs={12} sm={12} md={12}>
                        <Typography variant="h3">Events</Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12}>
                        {eventMessage}
                    </Grid>
                    {buttonAction && (
                        <Grid item xs={12} sm={12} md={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={buttonAction}
                            >
                                {buttonLabel}
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </Box>
            <ModuleEventsBookDialog
                open={eventsDialogOpen}
                moduleId={moduleId}
                events={visibleEvents}
                onClose={() => setEventsDialogOpen(false)}
                onCancel={() => setEventsDialogOpen(false)}
            />
            <ConfirmDialog
                open={
                    !!confirmDialogOpen && !!confirmAction && !!confirmMessage
                }
                title={confirmTitle}
                content={confirmMessage}
                confirmButtonLabel="Confirm"
                onConfirm={handleConfirm}
                onCancel={() => setConfirmDialogOpen(false)}
                onClose={() => setConfirmDialogOpen(false)}
            />
        </CardContent>
    );
}

export default ModuleEvents;
